import { useEffect, useState } from "react";

import { Button } from "@/components/_ui/button";
import { Input } from "@/components/_ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/_ui/select";

import { useDatatableContext } from "../use-datatable-context";

import {
  displayTextForFilterMode,
  FilterByDateMode,
  FilterByDatePayload,
  validateGermanDateInput,
} from "./functions/filter-by-date";

export interface FilterByDateItem {
  value: string;
  mode: FilterByDateMode;
}

export interface FilterByDateConfigurationViewProps<TItem extends FilterByDateItem> {
  column: {
    id: string;
  };
  onFinished: () => void;
  defaultValue?: TItem;
  allowedFilterModes?: FilterByDateMode[];
  inputPlaceholder?: string;
}

export const FilterByDateConfigurationView = <TItem extends FilterByDateItem>({
  onFinished,
  defaultValue,
  allowedFilterModes = ["before", "after"],
  inputPlaceholder = "Geben Sie ein Datum ein ...",
  ...props
}: FilterByDateConfigurationViewProps<TItem>) => {
  const [query, setQuery] = useState<string>(defaultValue ? String(defaultValue.value) : "");

  const [parsedDateString, setParsedDateString] = useState<string | null>(null);
  const [selectedMode, setSelectedMode] = useState<FilterByDateMode>(defaultValue?.mode ?? "after");
  const [error, setError] = useState<string | null>(null);

  const { table } = useDatatableContext();
  const column = table.getColumn(props.column.id);

  useEffect(() => {
    const parsedDate = validateGermanDateInput(query);
    setParsedDateString(parsedDate.data ?? null);
  }, [query, setParsedDateString]);

  const setFilter = (payload: FilterByDatePayload) => {
    column?.setFilterValue(payload);
  };

  const handleCancelClicked = () => {
    onFinished();
  };

  const handleApplyClicked = () => {
    if (!parsedDateString) {
      setError("Bitte geben Sie ein gültiges Datum im Format dd.MM.yyyy ein");
      return;
    }

    setError(null);
    setFilter({
      type: "filter_by_date",
      mode: selectedMode,
      date: parsedDateString,
    });

    onFinished();
  };

  return (
    <div>
      <div className="flex items-center">
        <div className="mb-4 mr-3 text-sm">Zuletzt aktualisiert</div>
        <div className="mb-4 text-sm">
          <Select
            value={selectedMode}
            onValueChange={(value: FilterByDateMode) => {
              setSelectedMode(value);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder={displayTextForFilterMode(selectedMode)} />
            </SelectTrigger>
            <SelectContent>
              {allowedFilterModes.map((mode) => {
                return (
                  <SelectItem key={mode} value={mode}>
                    {displayTextForFilterMode(mode)}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="mb-4">
        <Input
          value={query}
          onChange={(e) => {
            setQuery(e.currentTarget.value);
          }}
          autoFocus
          placeholder={inputPlaceholder}
          className="ring-offset-backgroundtransition-colors inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-normal focus:outline-none focus:ring-0 focus-visible:outline-none  focus-visible:ring-0 focus-visible:ring-ring focus-visible:ring-offset-0 disabled:pointer-events-none disabled:opacity-50"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleApplyClicked();
            }
          }}
        />
        <div className="mt-2 text-xs text-red-500">{error}</div>
      </div>
      <div className="flex flex-row items-center justify-end gap-2">
        <Button
          size="xs"
          variant="outline"
          onClick={() => {
            handleCancelClicked();
          }}
        >
          Abbrechen
        </Button>
        <Button
          size="xs"
          variant="default"
          onClick={() => {
            handleApplyClicked();
          }}
        >
          Anwenden
        </Button>
      </div>
    </div>
  );
};
